var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { vritiApiClient } from '@/hoc/api/api-service';
import { publishSharedSession } from '@/utils/shared-session';
import { useConfigContext } from '@/hoc';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';
import { useToast } from '@/hoc';
import { useLocation } from 'react-router-dom';
import { loginInitialState, loginValidationSchema } from '@/constants/loginValidation';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Formik, Form } from 'formik';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { setAccountType, setEmailType } from '@/utils';
import { getSafeNextPath } from '@/utils/redirect';
import VaModal from './ui/va-modal/va-modal';
import ContinueLogin from './ui/continue-login/continue-login';
import EmailVerificationValidateOtpModal from './ui/email-verification-validate-otp-modal/email-verification-validate-otp-modal';
import EmailVerificationModal from './email-verification-modal/email-verification-modal';
import { AuthShell, AuthSubmit, landingFieldSx } from './ai-landing/AuthShell';
import { writeAccessToken, writeRefreshToken, writeUserDetails } from '@/utils/auth-token-store';
const LoginScreen = () => {
    const { config: { shouldHaveAddScheduleBtn }, } = useConfigContext();
    const inputRef = useRef();
    const location = useLocation();
    const formRef = useRef(null);
    // Present when an MCP client sent the recruiter here to sign in and
    // authorize a connection (see mcp_server/oauth/browser.py::authorize on the
    // backend). mcp_return is the absolute URL of the backend's own
    // /api/mcp/oauth/complete -- this page never constructs that URL itself,
    // only echoes back what it was given, so it needs no per-environment
    // configuration.
    const mcpParams = new URLSearchParams(location.search);
    const mcpAuth = mcpParams.get('mcp_auth');
    const mcpReturn = mcpParams.get('mcp_return');
    const [loginInitialData, setLoginInitialData] = useState(loginInitialState);
    const [emailVerifyOtpVisible, setEmailVerifyOtpVisible] = useState(false);
    const [openEmailModal, setOpenEmailModal] = useState(false);
    const [afterVerifyPopup, setAfterVerifyPopup] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
    });
    const { showToast } = useToast();
    const navigate = useNavigate();
    const handleLogin = (values) => { };
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = (event) => event.preventDefault();
    const { mutate: verifyEmail, isPending: isVerifyEmailPending } = useMutation({
        mutationFn: (data) => vritiApiClient.post(`auth/send-verify-email/`, Object.assign({}, data), { skipAppName: true }),
        onSuccess: (data, variables) => {
            showToast({
                message: data.message,
                status: 'success',
            });
            setEmailVerifyOtpVisible(true);
            setOpenEmailModal(false);
        },
        onError: (error) => {
            showToast({
                message: 'Error in email verification',
                description: error.message,
                status: 'error',
            });
        },
    });
    const { mutate: getUserProfile, isPending: isUserProfilePending } = useMutation({
        mutationFn: () => vritiApiClient.get(`get-organization-profile/`),
        onSuccess: (data, variables) => {
            writeUserDetails(JSON.stringify(data === null || data === void 0 ? void 0 : data.data));
            showToast({
                message: data === null || data === void 0 ? void 0 : data.message,
                status: 'success',
            });
        },
        onError: (error) => { },
    });
    // Hands the just-obtained access token to the backend's OAuth authorization
    // server so it can bind it to the pending MCP connection request and send
    // the browser on to the consent screen. Nothing from this login is written
    // to this app's own storage or dispatched as an authChange for this path --
    // the browser is here only to authorize a third-party MCP client, not to
    // start a session in this app itself.
    const completeMcpAuthorization = (accessToken) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const response = yield fetch(mcpReturn, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mcp_auth: mcpAuth, access_token: accessToken }),
            });
            const result = yield response.json().catch(() => null);
            if (!response.ok || !(result === null || result === void 0 ? void 0 : result.redirect_to)) {
                showToast({
                    message: 'Could not complete the connection request.',
                    description: (result === null || result === void 0 ? void 0 : result.error_description) || '',
                    status: 'error',
                });
                return;
            }
            window.location.href = result.redirect_to;
        }
        catch (error) {
            showToast({
                message: 'Could not reach the server to complete the connection request.',
                status: 'error',
            });
        }
    });
    const { mutate, isPending } = useMutation({
        mutationFn: (data) => vritiApiClient.post(`auth/login/`, Object.assign(Object.assign({}, data), { from: 'organization' })),
        onSuccess: (data, variables) => {
            if (data === null || data === void 0 ? void 0 : data.account_type) {
                setAccountType(data);
            }
            if ((data === null || data === void 0 ? void 0 : data.account_type) === 'candidate') {
                showToast({
                    message: 'Candidate account!',
                    description: 'Please login with recruiter login credentials',
                    status: 'error',
                });
                return;
            }
            if (mcpAuth && mcpReturn) {
                if (data === null || data === void 0 ? void 0 : data.access) {
                    completeMcpAuthorization(data.access);
                }
                else {
                    showToast({ message: 'Login did not return a usable session.', status: 'error' });
                }
                return;
            }
            if (data === null || data === void 0 ? void 0 : data.access) {
                writeAccessToken(data.access);
                // Persist the refresh token so a fresh access token can be minted on
                // app load (see refreshAccessToken in api-service).
                if (data === null || data === void 0 ? void 0 : data.refresh) {
                    writeRefreshToken(data.refresh);
                }
                // Share the session with recruiter.vriti.ai so it opens signed in too.
                publishSharedSession(data.access, (data === null || data === void 0 ? void 0 : data.account_type) || 'organization');
                window.dispatchEvent(new Event('authChange'));
                getUserProfile();
            }
            // ✅ Redirect immediately after successful login. If the user arrived via a
            // protected link (e.g. the report link in the completion email), return
            // them to that `?next=` target instead of the dashboard.
            const nextPath = getSafeNextPath(location.search);
            navigate(nextPath || '/dashboard', { replace: true });
        },
        onError: (error) => {
            const err = error;
            if (err === null || err === void 0 ? void 0 : err.is_email_verified) {
                // enqueueSnackbar(loginActionState.message, { variant: "error", ...snackbarConfig(closeSnackbar) });
                writeAccessToken(err.access);
                window.dispatchEvent(new Event('authChange'));
                setEmailType(err === null || err === void 0 ? void 0 : err.data);
                setOpenEmailModal(false);
            }
            else if ((err === null || err === void 0 ? void 0 : err.is_email_verified) === false) {
                setOpenEmailModal(true);
            }
            else {
                showToast({ message: err.message, status: 'error' });
            }
        },
    });
    const { mutate: checkUserType, isPending: ischeckUserType } = useMutation({
        mutationFn: (data) => vritiApiClient.get(`auth/is-password-exists/?email=${data}`),
        onSuccess: (data, variables) => {
            if (data === null || data === void 0 ? void 0 : data.account_type) {
                setAccountType(data);
            }
        },
        onError: (error) => {
            showToast({ message: error === null || error === void 0 ? void 0 : error.message, status: 'error' });
        },
    });
    return (_jsxs(_Fragment, { children: [_jsx(VaModal, { setVisible: setAfterVerifyPopup, visible: afterVerifyPopup, children: _jsx(ContinueLogin, { setVisible: setAfterVerifyPopup, emailVerified: true }) }), emailVerifyOtpVisible ? (_jsx(EmailVerificationValidateOtpModal, { id: 'emailVerifyOtpVisible', visible: emailVerifyOtpVisible, setVisible: setEmailVerifyOtpVisible, formData: formData, setFormData: setFormData, setSuccessfulSignUp: () => { }, setExitEmail: () => { }, setSecondTimeSignupPopup: setAfterVerifyPopup, setEmailVerifyOtpVisible: setEmailVerifyOtpVisible })) : (''), _jsx(EmailVerificationModal, { title: 'Verify email', content: 'Your Email address is not verified, do you want to verify now?', visible: openEmailModal, onClose: () => setOpenEmailModal(false), loading: isVerifyEmailPending, onConfirm: () => {
                    let payload = {
                        email: formData.email, // ✅ non-null assertion
                        account_type: 'organization',
                    };
                    verifyEmail(payload);
                } }), _jsx(AuthShell, { kicker: 'Recruiter sign-in', title: 'Welcome back', footer: 'Vriti.AI \u2014 Secure recruiter sign-in', children: _jsx(Formik, { initialValues: loginInitialData, validationSchema: loginValidationSchema, onSubmit: (values) => {
                        const loginData = Object.assign(Object.assign({}, values), { account_type: 'organization' });
                        mutate(loginData);
                    }, children: ({ errors, touched, handleChange, handleBlur, values }) => (_jsxs(Form, { className: 'vx-auth__form', noValidate: true, children: [_jsxs("div", { className: 'text-left', children: [_jsx("label", { className: 'vx-auth__label', htmlFor: 'login-email', children: "Email ID" }), _jsx(TextField, { fullWidth: true, variant: 'outlined', size: 'medium', id: 'login-email', name: 'email', autoComplete: 'email', placeholder: 'you@company.com', value: values.email, onChange: handleChange, onBlur: handleBlur, error: touched.email && Boolean(errors.email), helperText: touched.email && errors.email, sx: landingFieldSx })] }), _jsxs("div", { className: 'text-left', children: [_jsxs("div", { className: 'vx-auth__labelrow', children: [_jsx("label", { className: 'vx-auth__label', htmlFor: 'login-password', children: "Password" }), _jsx("button", { type: 'button', onClick: () => navigate('/forgot-password'), className: 'vx-auth__link', children: "Forgot password?" })] }), _jsx(TextField, { fullWidth: true, variant: 'outlined', size: 'medium', type: showPassword ? 'text' : 'password', id: 'login-password', name: 'password', autoComplete: 'current-password', value: values.password, placeholder: 'Enter your password', onChange: handleChange, onBlur: handleBlur, error: touched.password && Boolean(errors.password), helperText: touched.password && errors.password, sx: landingFieldSx, InputProps: {
                                            endAdornment: (_jsx(InputAdornment, { position: 'end', children: _jsx(IconButton, { onClick: handleClickShowPassword, edge: 'end', "aria-label": showPassword ? 'Hide password' : 'Show password', children: showPassword ? _jsx(VisibilityOff, {}) : _jsx(Visibility, {}) }) })),
                                        } })] }), _jsx(AuthSubmit, { pending: isPending, label: 'Sign in', pendingLabel: 'Signing in\u2026' }), _jsxs("p", { className: 'vx-auth__alt', children: ["Don\u2019t have an account yet?", ' ', _jsx("button", { type: 'button', onClick: () => navigate('/signup'), className: 'vx-auth__link', children: "Create an account" })] })] })) }) })] }));
};
export default LoginScreen;
